import {
    SmartlyActionItems,
    canClone,
    canSaveDraft,
    canClose,
    canReject,
    canCancel,
    canRelease,
    canRedo,
    canEdit,
    canSendInApproval,
    canConfirmReadingNews,
    canSaveAsToBeReleased,
    canCreate,
    canRecall,
    canCreateReleased,
    canAcceptApproval,
    canSendInApprovalPhotographicTask,
    SmartlyActionSubtypes,
    User,
    LayoutActivityType,
} from '@Techedge/digitalportal-shared'
import { NewActivityCreationActions } from '../redux/actions/activity-creation'
import { AlbumsActions } from '../redux/actions/albums'
import { NewActivityCreationCta, NewActivityCreationCtaConfig } from './new-activity-creation-state'
import { ProjectApiStatus } from './projects-service'
import { SectionFilter } from './search'
import { StoreProps } from './store'

// CREATION
export const createAsDraft = (item: SmartlyActionItems, model: any) =>
    new NewActivityCreationCta({
        label: 'modals.new.activity.creation.cta.save.as.draft',
        defaultLabel: 'Save as Draft',
        action: NewActivityCreationActions.CREATE_AS_DRAFT,
        permission: (item: SmartlyActionItems, model: any) => {
            return (
                (!model.status ||
                    model.status === SectionFilter.draft ||
                    model.status === SectionFilter.rejected) &&
                (canCreate(item, model) || canCreateReleased(item, model))
            )
        },
        subject: item,
        model,
        icon: 'arrow-down-circle',
    })

export const saveAsDraft = (item: SmartlyActionItems, model: any) => {
    const isPhotographicTask = model?.module === 'photographic'
    return new NewActivityCreationCta({
        label: 'modals.new.activity.creation.cta.save.as.draft',
        defaultLabel: 'Save as Draft',
        action: NewActivityCreationActions.SAVE_AS_DRAFT,
        permission: (item: SmartlyActionItems, model: any) => {
            return (
                (!model.status ||
                    model.status === SectionFilter.draft ||
                    model.status === SectionFilter.rejected) &&
                canSaveDraft(item, model)
            )
        },
        subject: item,
        model,
        icon: 'arrow-down-circle',
    })
}

export const clone = (item: SmartlyActionItems, model: any) =>
    new NewActivityCreationCta({
        label: 'modals.new.activity.creation.cta.clone',
        defaultLabel: 'Clone',
        action: NewActivityCreationActions.CLONE,
        permission: canClone,
        subject: item,
        model,
        icon: 'copy',
        variant: 'outlined',
    })

export const preview = (item: SmartlyActionItems, model: any) =>
    new NewActivityCreationCta({
        label: 'modals.new.activity.creation.cta.preview',
        defaultLabel: 'Preview',
        action: AlbumsActions.SHOW_ALBUM_PREVIEW, // TODO
        permission: () => true,
        subject: item,
        model,
        variant: 'text',
    })

export const saveAlbum = (item: SmartlyActionItems, model: any) =>
    new NewActivityCreationCta({
        label: 'modals.new.activity.creation.cta.save_album',
        defaultLabel: 'Save',
        action: model.isEdit ? AlbumsActions.SAVE_EDITED_ALBUM : AlbumsActions.SAVE_NEW_ALBUM,
        permission: () => true, // TODO
        subject: item,
        model,
    })

export const createAsToBeReleased = (item: SmartlyActionItems, model: any) =>
    new NewActivityCreationCta({
        label: 'modals.new.activity.creation.cta.save.as.to.be.released',
        defaultLabel: 'Save as To be Released',
        action: NewActivityCreationActions.CREATE_AS_TO_BE_RELEASED,
        permission: (item: SmartlyActionItems, model: any) => {
            return (
                !release(item, model).show() &&
                (!model.status || model.status === 'draft' || model.status === 'rejected') &&
                canCreate(item, model)
            )
        },
        subject: item,
        model,
        icon: 'check-circle',
    })

export const createAsReleased = (item: SmartlyActionItems, model: any) => {
    const isPhotographicTask = model?.module === 'photographic'
    return new NewActivityCreationCta({
        label: isPhotographicTask
            ? 'ph2.modals.new.activity.creation.cta.conclude'
            : 'modals.new.activity.creation.cta.release',
        defaultLabel: isPhotographicTask ? 'Conclude' : 'Approve Project',
        action: NewActivityCreationActions.CREATE_AS_TO_BE_RELEASED,
        permission: (item: SmartlyActionItems, model: any) => {
            return (
                // isPhotographicTask || // TODO vere permission
                !release(item, model).show() &&
                (!model.status || model.status === 'draft' || model.status === 'rejected') &&
                canCreateReleased(item, model)
            )
        },
        subject: item,
        model,
        icon: 'check-circle',
    })
}

export const saveAsToBeReleased = (item: SmartlyActionItems, model: any) => {
    const isPhotographicTask = model?.module === 'photographic'
    return new NewActivityCreationCta({
        label: isPhotographicTask
            ? 'ph2.modals.new.activity.creation.cta.conclude'
            : 'modals.new.activity.creation.cta.save.as.to.be.released',
        defaultLabel: isPhotographicTask ? 'Conclude' : 'Save as To be Released',
        action: NewActivityCreationActions.SAVE_AS_TO_BE_RELEASED,
        permission: (item: SmartlyActionItems, model: any) => {
            return (
                // isPhotographicTask || // TODO vere permission
                !release(item, model).show() && canSaveAsToBeReleased(item, model)
            )
        },
        subject: item,
        model,
        icon: 'check-circle',
    })
}

export const editSaveAsDraft = (item: SmartlyActionItems, model: any) =>
    new NewActivityCreationCta({
        label: 'modals.new.activity.creation.cta.save.as.draft',
        defaultLabel: 'Save as Draft',
        action: NewActivityCreationActions.EDIT_SAVE_AS_DRAFT,
        permission: () =>
            model.status === SectionFilter.draft || model.status === SectionFilter.rejected,
        subject: item,
        model,
        icon: 'arrow-down-circle',
    })
export const editSaveAsToBeReleased = (item: SmartlyActionItems, model: any) =>
    new NewActivityCreationCta({
        label: 'modals.new.activity.creation.cta.save.as.to.be.released',
        defaultLabel: 'Save as To be Released',
        action: NewActivityCreationActions.EDIT_SAVE_AS_TO_BE_RELEASED,
        permission: () => true,
        subject: item,
        model,
        icon: 'check-circle',
    })

export const editRelease = (item: SmartlyActionItems, model: any) =>
    new NewActivityCreationCta({
        label: 'modals.new.activity.creation.cta.release',
        defaultLabel: 'Release',
        action: NewActivityCreationActions.EDIT_RELEASE,
        permission: () => true,
        subject: item,
        model,
        icon: 'agenda',
    })

export const historyChange = (item: SmartlyActionItems, model: any) =>
    new NewActivityCreationCta({
        label: 'modals.new.activity.creation.cta.history.change',
        defaultLabel: 'History change',
        action: NewActivityCreationActions.HISTORY_CHANGE,
        permission: (item, model) => {
            return (
                !!model.status &&
                model.status !== 'draft' &&
                model.status !== 'toBeReleased' &&
                model.status !== 'rejected'
            )
        },
        subject: item,
        model,
        variant: 'outline',
        icon: 'note',
    })

export const cancel = (item: SmartlyActionItems, model: any) =>
    new NewActivityCreationCta({
        label: 'modals.new.activity.creation.cta.cancel',
        defaultLabel: 'Delete',
        action: NewActivityCreationActions.CANCEL,
        permission: canCancel,
        subject: item,
        model,
        variant: 'outline',
        icon: 'trash',
    })

export const cancelVisit = (item: SmartlyActionItems, model: any, user: User) =>
    new NewActivityCreationCta({
        label: 'modals.new.activity.creation.cta.cancel',
        defaultLabel: 'Delete',
        action: NewActivityCreationActions.CANCEL,
        permission: (item: SmartlyActionItems, model: any) => {
            return (
                model.status !== SectionFilter.closed &&
                (canCancel(item, model) || model.assignmentUserIds.includes(user.user_id))
            )
        },
        subject: item,
        model,
        variant: 'outline',
        icon: 'trash',
    })

// RELEASE
export const release = (item: SmartlyActionItems, model: any) => {
    return new NewActivityCreationCta({
        label: 'modals.new.activity.creation.cta.release',
        defaultLabel: 'Release',
        action: NewActivityCreationActions.RELEASE,
        permission: canRelease,
        subject: item,
        model,
        icon: 'agenda',
    })
}
export const reject = (item: SmartlyActionItems, model: any) =>
    new NewActivityCreationCta({
        label: 'modals.execution.button.reject',
        defaultLabel: 'Reject',
        action: NewActivityCreationActions.REJECT,
        permission: canReject,
        subject: item,
        model,
        icon: 'ban',
    })

// EXECUTION
export const saveAsInProgress = (item: SmartlyActionItems, model: any) =>
    new NewActivityCreationCta({
        label: 'modals.news_execution.button.save_as_in_progress',
        defaultLabel: 'Save as in Progress',
        icon: 'loading',
        action: NewActivityCreationActions.SAVE_AS_IN_PROGRESS,
        permission: (item: SmartlyActionItems, model: any) => true, //TO DO: da verificare
    })

export const saveAsInApprovalNews = (item: SmartlyActionItems, model: any) => {
    return new NewActivityCreationCta({
        label: 'modals.news_execution.button.save_as_in_approval',
        defaultLabel: 'Save as in Approval',
        action: NewActivityCreationActions.SAVE_AS_IN_APPROVAL,
        permission: canSendInApproval,
        icon: 'check-refresh',
        subject: item,
        model,
    })
}

export const saveAsInApproval = (item: SmartlyActionItems, model: any, user?: User) => {
    return new NewActivityCreationCta({
        label: 'modals.news_execution.button.save_as_in_approval',
        defaultLabel: 'Save as in Approval',
        action: NewActivityCreationActions.SAVE_AS_IN_APPROVAL,
        permission: () => {
            const acceptedStatus = [SectionFilter.inProgress, SectionFilter.overdue]
            if (model.type === SmartlyActionSubtypes.COMMUNICATION) {
                return acceptedStatus.includes(model.storesList[0].status)
            }
            const hasAccepted = model.storesList[0]?.executorUserInfo?.find(
                (u) => u.userId === user.user_id && u.isAccepted
            )
            if (!hasAccepted) return false
            return (
                acceptedStatus.includes(model.storesList[0]?.status) &&
                (canSendInApproval(item, model) ||
                    model.storesList[0]?.executorUserInfo?.find(
                        (u) => u.userId === user.user_id && u.isAccepted
                    ))
            )
        },
        icon: 'check-refresh',
        subject: item,
        model,
    })
}
export const saveAsInApprovalPhotographicTask = (item: SmartlyActionItems, model: any) => {
    return new NewActivityCreationCta({
        label: model.approverRequired
            ? 'modals.news_execution.button.save_as_in_approval'
            : 'modals.news_execution.button.save',
        defaultLabel: model.approverRequired ? 'Save as in Approval' : 'Save',
        action: NewActivityCreationActions.SAVE_AS_IN_APPROVAL,
        permission: canSendInApprovalPhotographicTask,
        icon: 'check-refresh',
        subject: item,
        model,
    })
}

// Photographic Accept/Do not accept
export const acceptPhotographicTask = (item: SmartlyActionItems, model: any, user: User) => {
    return new NewActivityCreationCta({
        label: 'modals.news_execution.button.accept.photographic.task',
        defaultLabel: 'Accept',
        action: NewActivityCreationActions.ACCEPT_PHOTOGRAPHIC_TASK,
        permission: () => {
            if (
                model.type === SmartlyActionSubtypes.COMMUNICATION ||
                model.type === LayoutActivityType.SURVEY_TASK
            )
                return false
            const userHasAccepted = model.storesList[0]?.executorUserInfo?.find(
                (u) => u.userId === user.user_id && u.isAccepted
            )
            if (userHasAccepted) return false
            return (
                model.assignment.includes(user.business_function) ||
                model.storesList[0]?.executorUserInfo?.filter((u) => {
                    return (
                        !userHasAccepted &&
                        ((!u.userId && u.role === user.business_function && !u.isAccepted) ||
                            (!!u.userId && u.userId === user.user_id && !u.isAccepted))
                    )
                }).length > 0
            )
        },
        icon: 'check-refresh',
        subject: item,
        model,
    })
}

export const doNotAcceptPhotographicTask = (item: SmartlyActionItems, model: any, user: any) => {
    return new NewActivityCreationCta({
        label: 'modals.news_execution.button.do.not.accept.photographic.task',
        defaultLabel: 'Accept',
        action: NewActivityCreationActions.DO_NOT_ACCEPT_PHOTOGRAPHIC_TASK,
        permission: () => {
            const userHasAccepted = model.storesList[0]?.executorUserInfo?.find(
                (u) => u.userId === user.user_id && u.isAccepted
            )
            return (
                model.storesList[0]?.executorUserInfo?.filter((u) => {
                    return (
                        !userHasAccepted &&
                        ((!u.userId && u.role === user.business_function && !u.isAccepted) ||
                            (!!u.userId && u.userId === user.user_id && !u.isAccepted))
                    )
                }).length > 0
            )
        },
        icon: 'times',
        subject: item,
        model,
    })
}

export const acceptApprovalServiceRequestApprovalDetailTask = (
    item: SmartlyActionItems,
    model: any,
    user: User
) => {
    return new NewActivityCreationCta({
        label: 'modals.news_execution.button.accept.photographic.task',
        defaultLabel: 'Accept',
        action: NewActivityCreationActions.ACCEPT_APPROVAL_SERVICE_REQUEST_APPROVAL_DETAIL,
        permission: () => {
            const hasAccepted =
                model.storesList.length === 1 &&
                model.storesList[0]?.approvalUserInfo?.find(
                    (u) => u.userId === user.user_id && u.isAccepted
                )
            if (hasAccepted) {
                return false
            }
            return (
                canAcceptApproval(item, model) ||
                model.delegatedApprovalUserIds?.indexOf(user.user_id) > -1 ||
                model.storesList[0]?.approvalUserInfo?.find(
                    (u) => u.role === user.business_function
                )
            )
        },
        icon: 'check-refresh',
        subject: item,
        model,
    })
}

export const acceptApprovalPhotographicTask = (
    item: SmartlyActionItems,
    model: any,
    user: User,
    store?: StoreProps
) => {
    return new NewActivityCreationCta({
        label: 'modals.news_execution.button.accept.photographic.task',
        defaultLabel: 'Accept',
        action: NewActivityCreationActions.ACCEPT_APPROVAL_PHOTOGRAPHIC_TASK,
        permission: () => {
            if (
                model.type === SmartlyActionSubtypes.COMMUNICATION ||
                model.type === LayoutActivityType.SURVEY_TASK
            )
                return false
            const hasAccept = model.approvalUserInfo?.find(
                (u) => u.userId === user.user_id && u.isAccepted
            )
            if (hasAccept) return false
            return (
                canAcceptApproval(item, model) ||
                store?.isEnabledApprove ||
                model.approvalUserInfo?.find(
                    (u) => (u.role === user.role && !u.userId) || u.userId === u.user_id
                )
            )
        },
        icon: 'check-refresh',
        subject: item,
        model,
    })
}

export const doNotAcceptApprovalPhotographicTask = (
    item: SmartlyActionItems,
    model: any,
    user: User,
    store?: StoreProps
) => {
    return new NewActivityCreationCta({
        label: 'modals.news_execution.button.do.not.accept.photographic.task',
        defaultLabel: 'Refuse',
        action: NewActivityCreationActions.DO_NOT_ACCEPT_APPROVAL_PHOTOGRAPHIC_TASK,
        permission: () =>
            (canAcceptApproval(item, model) ||
                store?.isEnabledApprove ||
                model.delegatedApprovalUserIds?.indexOf(user.user_id) > -1) &&
            !model.approvalUserInfo?.filter((u) => u.isAccepted).length,
        icon: 'times',
        subject: item,
        model,
    })
}

export const markAsRead = (item: SmartlyActionItems, model: any) =>
    new NewActivityCreationCta({
        label:
            !!model && model.status !== ProjectApiStatus.READ
                ? 'modals.new.activity.execution.messaging.cta.mark.as.read'
                : 'modals.new.activity.execution.messaging.cta.mark.as.unread',
        defaultLabel: 'Mark as Read',
        action: NewActivityCreationActions.MARK_AS_READ,
        permission: (item, model) =>
            !!model && model.requiredConfirmReading && canConfirmReadingNews(item, model),
        subject: item,
        model,
        icon: 'eye',
    })

// APPROVAL
export const closeVisitProject = (item: SmartlyActionItems, model: any, user: User) =>
    new NewActivityCreationCta({
        label: 'modals.execution.button.close_project',
        defaultLabel: 'Close project',
        action: NewActivityCreationActions.CLOSE_PROJECT,
        permission: (item, model) =>
            canClose(item, model) && model.assignmentUserIds.includes(user.user_id),
        subject: item,
        icon: 'times',
        model,
    })
export const closeProject = (item: SmartlyActionItems, model: any, user: User) =>
    new NewActivityCreationCta({
        label: 'modals.execution.button.close_project',
        defaultLabel: 'Close project',
        action: NewActivityCreationActions.CLOSE_PROJECT,
        permission: (item, model) => {
            const hasAccept = model.storesList[0]?.approvalUserInfo?.find(
                (u) => u.userId === user.user_id && u.isAccepted
            )
            if (model.type === SmartlyActionSubtypes.SERVICE_REQUEST && !hasAccept) {
                return false
            }
            return canClose(item, model)
        },
        subject: item,
        icon: 'times',
        model,
    })
export const recallProject = (item: SmartlyActionItems, model: any) =>
    new NewActivityCreationCta({
        label: 'modals.execution.button.recall_project',
        defaultLabel: 'Recall Project',
        action: NewActivityCreationActions.RECALL_PROJECT,
        permission: canRecall,
        subject: item,
        icon: 'recall',
        model,
    })
export const edit = (item: SmartlyActionItems, model: any) =>
    new NewActivityCreationCta({
        label: 'modals.execution.button.edit',
        defaultLabel: 'Edit',
        action: NewActivityCreationActions.EDIT,
        permission: canEdit,
        subject: item,
        model,
        icon: 'pencil',
        variant: 'outline',
    })
// APPROVAL STORE (probabilmente fissi nella vista relativa)
export const redoAll = (item: SmartlyActionItems, model: any, user: User) =>
    new NewActivityCreationCta({
        label: 'modals.execution.button.redo_all',
        defaultLabel: 'Redo All',
        action: NewActivityCreationActions.REDO_ALL,
        permission: () => {
            return (
                (canRedo(item, model) && !canAcceptApproval(item, model)) ||
                (canRedo(item, model) &&
                    canAcceptApproval(item, model) &&
                    model.storesList.filter((s) =>
                        s.approvalUserInfo?.find((a) => a.userId === user.user_id)
                    ).length > 0)
            )
        },
        subject: item,
        model,
    })

export const closeAll = (item: SmartlyActionItems, model: any, user: User) => {
    return new NewActivityCreationCta({
        label: 'modals.execution.button.close_all',
        defaultLabel: 'Close All',
        action: NewActivityCreationActions.CLOSE_ALL,
        permission: () => {
            const hasAccept = model.storesList[0]?.approvalUserInfo?.find(
                (u) => u.userId === user.user_id && u.isAccepted
            )
            if (model.type === SmartlyActionSubtypes.SERVICE_REQUEST && !hasAccept) {
                return false
            }
            return (
                (canClose(item, model) && !canAcceptApproval(item, model)) ||
                (canAcceptApproval(item, model) &&
                    model.storesList.filter((s) =>
                        s.approvalUserInfo?.find((a) => a.userId === user.user_id)
                    ).length > 0)
            )
        },
        subject: item,
        model,
        icon: 'times',
    })
}

export const reassignProject = (item: SmartlyActionItems, model: any, user: any) => {
    return new NewActivityCreationCta({
        label: 'ph2.modals.execution.button.reassign',
        defaultLabel: 'Reassign',
        action: NewActivityCreationActions.REASSIGN,
        permission: () => {
            if (model.status === SectionFilter.closed) return false
            if (item === SmartlyActionItems.NEWS) return false
            if (
                model?.type === SmartlyActionSubtypes.COMMUNICATION &&
                !model.jobs.length &&
                !model.questions.length &&
                !model.surveys.length
            )
                return false
            return (
                // ((model?.type === SmartlyActionSubtypes.PICTURE_MGMT &&
                //     model?.storesList.find(
                //         (s) => s.status === SectionFilter.new || s.status === SectionFilter.overdue
                //     )) ||
                //     ((model?.type === SmartlyActionSubtypes.COMMUNICATION ||
                //         model?.type === SmartlyActionSubtypes.SERVICE_REQUEST ||
                //         model?.type === LayoutActivityType.SURVEY_TASK) &&
                //         model?.storesList.find(
                //             (s) =>
                //                 s.status === SectionFilter.inProgress ||
                //                 s.status === SectionFilter.new ||
                //                 s.status === SectionFilter.overdue
                //         ))) &&
                !!model?.assignment?.find((a) => a === user.business_function) ||
                !!model?.assignmentUserIds?.find((a) => a === user.user_id) ||
                !!model?.delegatedExecutorUserIds?.find((a) => a === user.user_id)
            )
        },
        subject: item,
        model,
        icon: 'refresh',
        variant: 'outlined',
    })
}

export const reassignVisitProject = (item: SmartlyActionItems, model: any, user: any) => {
    return new NewActivityCreationCta({
        label: 'ph2.modals.execution.button.reassign',
        defaultLabel: 'Reassign',
        action: NewActivityCreationActions.REASSIGN,
        permission: () => {
            const storeStatus = model?.storesList[0].status
            return (
                (storeStatus === SectionFilter.inProgress ||
                    storeStatus === SectionFilter.new ||
                    storeStatus === SectionFilter.overdue) &&
                (model?.creatorUserInfo?.userId === user.user_id ||
                    !!model?.assignmentUserIds?.find((a) => a === user.user_id))
            )
        },
        subject: item,
        model,
        icon: 'refresh',
        variant: 'outlined',
    })
}

// PERMISSION LIST
export const creationCta = (item: SmartlyActionItems, model: any) => {
    return new NewActivityCreationCtaConfig({
        left: [cancel(item, model), clone(item, model)],
        right: [
            !saveAsDraft(item, model).show()
                ? createAsDraft(item, model)
                : saveAsDraft(item, model),
            createAsReleased(item, model),
            !saveAsToBeReleased(item, model).show()
                ? createAsToBeReleased(item, model)
                : saveAsToBeReleased(item, model),
            release(item, model),
        ],
    })
}

export const creationSurveyTaskCta = (item: SmartlyActionItems, model: any) => {
    return new NewActivityCreationCtaConfig({
        left: [cancel(item, model), clone(item, model)],
        right: [createAsReleased(item, model)],
    })
}

export const editCta = (item: SmartlyActionItems, model: any) => {
    return new NewActivityCreationCtaConfig({
        left: !historyChange(item, model).show()
            ? [cancel(item, model), clone(item, model)]
            : [historyChange(item, model)],
        right: [
            editSaveAsDraft(item, model),
            canCreateReleased(item, model)
                ? editRelease(item, model)
                : editSaveAsToBeReleased(item, model),
        ],
    })
}
export const executionCta = (
    item: SmartlyActionItems,
    model: any,
    user,
    storeId,
    isPhotographicApprovalAccept?
) => {
    let rightCta = []
    let leftCta = []
    if (!isPhotographicApprovalAccept) {
        rightCta = [
            markAsRead(item, model),
            saveAsDraft(item, model),
            saveAsToBeReleased(item, model),
            recallProject(item, model),
            reject(item, model),
            release(item, model),
            closeProject(item, model, user),
        ]
        leftCta = [
            edit(item, model),
            cancel(item, model),
            clone(item, model),
            reassignProject(item, model, user),
        ]
        if (storeId) {
            rightCta.push(saveAsInApproval(item, model, user))
            // rightCta.push(saveAsInApprovalPhotographicTask(item, model))
            rightCta.push(acceptPhotographicTask(item, model, user))
            leftCta.push(doNotAcceptPhotographicTask(item, model, user))
        }
    } else {
        rightCta.push(acceptApprovalPhotographicTask(item, model, user, model.storesList[0]))
        leftCta.push(doNotAcceptApprovalPhotographicTask(item, model, user, model.storesList[0]))
    }

    return new NewActivityCreationCtaConfig({
        left: leftCta,
        right: rightCta,
    })
}

export const executionVisitCta = (item: SmartlyActionItems, model: any, user: User) => {
    return {
        left: [
            edit(item, model),
            cancelVisit(item, model, user),
            clone(item, model),
            reassignVisitProject(item, model, user),
        ],
        right: [
            saveAsDraft(item, model),
            release(item, model),
            closeVisitProject(item, model, user),
        ],
    }
}

export const executionNewsCta = (item: SmartlyActionItems, model: any) => {
    return {
        left: [edit(item, model), cancel(item, model), clone(item, model)],
        right: [
            markAsRead(item, model),
            saveAsDraft(item, model),
            saveAsToBeReleased(item, model),
            saveAsInApprovalNews(item, model),
            recallProject(item, model),
            reject(item, model),
            release(item, model),
        ],
    }
}

export const approvalStoreCta = (item: SmartlyActionItems, model: any, user: User) => {
    return new NewActivityCreationCtaConfig({
        left: [redoAll(item, model, user)],
        right: [
            closeAll(item, model, user),
            acceptApprovalServiceRequestApprovalDetailTask(item, model, user),
        ],
    })
}

export const approvalCta = (item: SmartlyActionItems, model: any, user: User) => {
    const left = [
        clone(item, model),
        edit(item, model),
        doNotAcceptApprovalPhotographicTask(item, model, user),
    ]
    const right = [
        closeProject(item, model, user),
        recallProject(item, model),
        acceptApprovalPhotographicTask(item, model, user),
    ]
    return new NewActivityCreationCtaConfig({
        left,
        right,
    })
}

export const albumCreationCta = (item: SmartlyActionItems, model: any) => {
    return new NewActivityCreationCtaConfig({
        left: [preview(item, model)],
        right: [saveAlbum(item, model)],
    })
}
